import React, { useState } from 'react'
import { formatRelativeTime } from '../hooks.js'

/**
 * ReverbThreadSection — renders the buyer↔seller Reverb conversation pulled
 * via API, with a callout for the latest message and the full thread below
 * for context. Hidden when no thread is available.
 */
function ReverbThreadSection({ thread, status, reason, fetchedAt, refreshing, onRefresh, compact = false }) {
  // Phone (`compact`) variant: collapse the whole thread to a one-liner
  // by default. Tap to expand back to the full thread render below.
  const [expanded, setExpanded] = useState(false)

  if (status === 'idle') return null

  if (status === 'loading') {
    return (
      <div style={{ background: 'white', padding: 16, borderRadius: 8, marginBottom: 16, fontSize: 12, color: 'var(--muted)' }}>
        Loading Reverb conversation…
      </div>
    )
  }

  if (status === 'unavailable' || status === 'error' || !thread) {
    if (status === 'error') {
      return (
        <div style={{ background: '#fff5f5', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 12, color: '#c53030' }}>
          Couldn't load Reverb conversation: {reason || 'unknown error'}
        </div>
      )
    }
    // unavailable — show a short note so users (and we) know why nothing rendered
    return (
      <div style={{ background: '#fffbeb', padding: 10, borderRadius: 8, marginBottom: 16, fontSize: 11, color: '#92400e' }}>
        No Reverb thread loaded {reason ? `(${reason})` : ''}
      </div>
    )
  }

  const messages = thread.messages || []
  if (messages.length === 0) return null

  const latest = thread.latestMessage
  const awaiting = thread.awaitingReply

  // Compact (phone) mode — render a single tap-to-expand line. The full
  // thread JSX below is reused once the user expands.
  if (compact && !expanded) {
    return (
      <button
        type="button"
        className="reverb-compact"
        onClick={() => setExpanded(true)}
      >
        — Reverb thread ({messages.length} marketplace message{messages.length === 1 ? '' : 's'}) — tap to expand
      </button>
    )
  }

  return (
    <div style={{ background: 'white', padding: 16, borderRadius: 8, marginBottom: 16, border: '1px solid rgba(0,0,0,.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          Reverb conversation <span style={{ color: 'var(--muted)', fontWeight: 400 }}>· {messages.length} message{messages.length === 1 ? '' : 's'}</span>
        </div>
        <button
          className="btn btn-sm btn-ghost"
          onClick={onRefresh}
          disabled={refreshing}
          title={fetchedAt ? `Last fetched ${formatRelativeTime(fetchedAt)}` : 'Fetch latest from Reverb'}
          style={{ fontSize: 11, padding: '2px 8px' }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Newest-message callout */}
      {latest && (
        <div
          style={{
            padding: 12,
            borderRadius: 6,
            marginBottom: 16,
            background: awaiting ? '#fff8e1' : '#e8f5e9',
            borderLeft: `3px solid ${awaiting ? '#f59e0b' : '#10b981'}`
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, color: awaiting ? '#92400e' : '#065f46' }}>
            {awaiting ? '🔔 Awaiting your reply' : '✓ You replied'} · {formatRelativeTime(latest.sentAt)}
            {typeof latest.fromName === 'string' && latest.fromName && (
              <span style={{ marginLeft: 8, fontWeight: 400, color: 'var(--muted)' }}>from {latest.fromName}</span>
            )}
          </div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5 }}>
            {typeof latest.body === 'string' ? latest.body : JSON.stringify(latest.body)}
          </div>
        </div>
      )}

      {/* Full thread chronological — collapses the latest message since it's already in the callout */}
      {messages.length > 1 && (
        <details style={{ fontSize: 12 }}>
          <summary style={{ cursor: 'pointer', color: 'var(--muted)', marginBottom: 8 }}>
            View full thread ({messages.length - 1} earlier message{messages.length - 1 === 1 ? '' : 's'})
          </summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            {messages.slice(0, -1).map((m, i) => {
              const safeFromName = typeof m.fromName === 'string' ? m.fromName : null
              const safeBody = typeof m.body === 'string' ? m.body : JSON.stringify(m.body)
              return (
                <div
                  key={m.id || i}
                  style={{
                    padding: 10,
                    borderRadius: 6,
                    background: m.senderType === 'seller' ? '#e8f5e9' : '#f7f7f7',
                    marginLeft: m.senderType === 'seller' ? 24 : 0,
                    marginRight: m.senderType === 'seller' ? 0 : 24
                  }}
                >
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>
                    {m.senderType === 'seller' ? 'You' : (safeFromName || 'Buyer')} · {formatRelativeTime(m.sentAt)}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5 }}>{safeBody}</div>
                </div>
              )
            })}
          </div>
        </details>
      )}
    </div>
  )
}

export default ReverbThreadSection
